import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, query, where, orderBy, addDoc, deleteDoc, doc, updateDoc, Timestamp, getDocs } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail,
  Phone,
  FileText,
  FileIcon,
  Send,
  Plus,
  Trash2,
  Download,
  Clock,
  User,
  Pin,
  Upload,
  Paperclip,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmailDialog } from './EmailDialog';

interface Communication {
  id: string;
  applicationId?: string;
  merchantId?: string;
  type: 'email_sent' | 'phone_call' | 'note' | 'document';
  subject?: string;
  content?: string;
  description?: string;
  from?: string;
  to?: string;
  direction?: 'inbound' | 'outbound';
  phoneNumber?: string;
  duration?: string;
  documentUrl?: string;
  documentName?: string;
  documentSize?: number;
  documentType?: string;
  isPinned?: boolean;
  timestamp: Timestamp;
  createdAt?: Timestamp;
  createdBy?: string;
  userId?: string;
  createdByEmail?: string;
  metadata?: {
    subject?: string;
    recipientEmail?: string;
    content?: string;
    duration?: string;
    outcome?: 'successful' | 'no_answer' | 'follow_up_required' | 'voicemail' | 'other';
    notes?: string;
    agentId?: string;
    agentName?: string;
    isPinned?: boolean;
    pinnedAt?: Timestamp | null;
  };
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
  }>;
}

interface CommunicationsSectionProps {
  applicationId: string;
  tab: 'emails' | 'phone' | 'notes' | 'documents';
  merchantName?: string;
  merchantEmail?: string;
}

export function CommunicationsSection({ applicationId, tab, merchantName, merchantEmail }: CommunicationsSectionProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    to: '',
    phoneNumber: '',
    duration: '',
    direction: 'outbound' as 'inbound' | 'outbound',
  });
  // Inline composer state for notes
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNotePinned, setNewNotePinned] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (tab === 'notes' && noteInputRef.current) {
      noteInputRef.current.focus();
    }
  }, [tab]);

  // Fetch communications
  const { data: communications = [], isLoading } = useQuery({
    queryKey: ['communications', applicationId, tab],
    queryFn: async () => {
      try {
        // Use the same structure as wikipayit: leads/{merchantId}/communications
        const communicationsRef = collection(db, `merchantApplications/${applicationId}/communications`);
        
        let q;
        const communicationType = tab === 'notes' ? 'note' : tab === 'phone' ? 'phone_call' : tab === 'documents' ? 'document' : 'email_sent';
        
        try {
          // Try with compound query first
          q = query(
            communicationsRef,
            where('type', '==', communicationType),
            orderBy('timestamp', 'desc')
          );
          const snapshot = await getDocs(q);
          const comms = snapshot.docs.map(doc => {
            const data = doc.data() as Omit<Communication, 'id'>;
            return {
              id: doc.id,
              ...data
            };
          }) as Communication[];
          
          // Sort notes so pinned ones appear first
          if (tab === 'notes') {
            return comms.sort((a, b) => {
              if (a.metadata?.isPinned && !b.metadata?.isPinned) return -1;
              if (!a.metadata?.isPinned && b.metadata?.isPinned) return 1;
              return 0;
            });
          }
          
          return comms;
        } catch (indexError) {
          // If compound query fails due to missing index, try simple query
          console.warn('Compound query failed, trying simple query:', indexError);
          
          // Fetch all and filter in memory
          const snapshot = await getDocs(communicationsRef);
          const allComms = snapshot.docs.map(doc => {
            const data = doc.data() as Omit<Communication, 'id'>;
            return {
              id: doc.id,
              ...data
            };
          }) as Communication[];
          
          // Filter by type
          const filteredComms = allComms.filter(comm => comm.type === communicationType);
          
          // Sort by timestamp
          const sortedComms = filteredComms.sort((a, b) => {
            const aTime = a.timestamp?.toMillis() || 0;
            const bTime = b.timestamp?.toMillis() || 0;
            return bTime - aTime;
          });
          
          // Sort notes so pinned ones appear first
          if (tab === 'notes') {
            return sortedComms.sort((a, b) => {
              if (a.metadata?.isPinned && !b.metadata?.isPinned) return -1;
              if (!a.metadata?.isPinned && b.metadata?.isPinned) return 1;
              return 0;
            });
          }
          
          return sortedComms;
        }
      } catch (error) {
        console.error('Failed to fetch communications:', error);
        return [];
      }
    },
  });

  // Add communication mutation
  const addCommunicationMutation = useMutation({
    mutationFn: async (data: Partial<Communication>) => {
      const timestamp = Timestamp.now();
      const communicationsRef = collection(db, `merchantApplications/${applicationId}/communications`);
      
      await addDoc(communicationsRef, {
        type: tab === 'notes' ? 'note' : tab === 'phone' ? 'phone_call' : tab === 'documents' ? 'document' : 'email_sent',
        description: data.content || '',
        userId: user?.uid || '',
        merchantId: applicationId,
        timestamp,
        metadata: {
          ...data.metadata,
          content: data.content,
          ...(tab === 'emails' && {
            subject: data.subject,
            recipientEmail: data.to,
          }),
          agentName: user?.displayName || user?.email || 'Unknown Agent',
          agentId: user?.uid,
          isPinned: data.metadata?.isPinned || false,
          pinnedAt: data.metadata?.isPinned ? timestamp : null,
          ...(tab === 'phone' && {
            duration: data.duration,
            outcome: data.metadata?.outcome || 'other',
            notes: data.content,
          }),
        },
        ...(tab === 'documents' && {
          documentUrl: data.documentUrl,
          documentName: data.documentName,
          documentSize: data.documentSize,
          documentType: data.documentType,
        }),
        ...(tab === 'emails' && {
          attachments: data.attachments,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', applicationId, tab] });
      toast({
        title: 'Success',
        description: `${tab === 'notes' ? 'Note' : tab === 'phone' ? 'Phone call' : tab === 'documents' ? 'Document' : 'Email'} added successfully`,
      });
      setShowAddDialog(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to add communication:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add communication',
        variant: 'destructive',
      });
    },
  });

  // Delete communication mutation
  const deleteCommunicationMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, `merchantApplications/${applicationId}/communications`, id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', applicationId, tab] });
      toast({
        title: 'Success',
        description: 'Communication deleted successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete communication',
        variant: 'destructive',
      });
    },
  });

  // Pin/Unpin note mutation
  const togglePinMutation = useMutation({
    mutationFn: async ({ id, isPinned }: { id: string; isPinned: boolean }) => {
      await updateDoc(doc(db, `merchantApplications/${applicationId}/communications`, id), {
        'metadata.isPinned': !isPinned,
        'metadata.pinnedAt': !isPinned ? Timestamp.now() : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications', applicationId, tab] });
      toast({
        title: 'Success',
        description: 'Note updated successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update note',
        variant: 'destructive',
      });
    },
  });

  // File upload function
  const uploadFile = async (file: File) => {
    const storageRef = ref(storage, `merchant-applications/${applicationId}/${Date.now()}-${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return {
      url: downloadURL,
      name: file.name,
      size: file.size,
      type: file.type,
    };
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      content: '',
      to: '',
      phoneNumber: '',
      duration: '',
      direction: 'outbound',
    });
    setSelectedFile(null);
    setEmailAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEmailSend = async (emailData: {
    subject: string;
    content: string;
    to: string;
    attachments?: File[];
  }) => {
    try {
      const data: Partial<Communication> = {
        subject: emailData.subject,
        content: emailData.content,
        to: emailData.to,
        from: user?.email || '',
        direction: 'outbound' as 'inbound' | 'outbound',
        metadata: {
          subject: emailData.subject,
          recipientEmail: emailData.to,
          content: emailData.content,
          agentName: user?.displayName || user?.email || 'Unknown Agent',
          agentId: user?.uid,
        },
      };

      // Handle email attachments
      if (emailData.attachments && emailData.attachments.length > 0) {
        const attachments = await Promise.all(emailData.attachments.map(file => uploadFile(file)));
        data.attachments = attachments;
      }

      await addCommunicationMutation.mutateAsync(data);
      setShowEmailDialog(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send email',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    setUploadingFile(true);
    
    try {
      const data: Partial<Communication> = {
        content: formData.content,
        metadata: {},
      };

      if (tab === 'emails') {
        data.subject = formData.subject;
        data.to = formData.to;
        data.from = user?.email || '';
        data.direction = formData.direction;
        
        // Handle email attachments
        if (emailAttachments.length > 0) {
          const attachments = await Promise.all(emailAttachments.map(file => uploadFile(file)));
          data.attachments = attachments;
        }
      } else if (tab === 'phone') {
        data.phoneNumber = formData.phoneNumber;
        data.duration = formData.duration;
        data.direction = formData.direction;
        data.metadata = {
          outcome: 'other',
        };
      } else if (tab === 'documents' && selectedFile) {
        // Upload document
        const uploaded = await uploadFile(selectedFile);
        data.documentUrl = uploaded.url;
        data.documentName = uploaded.name;
        data.documentSize = uploaded.size;
        data.documentType = uploaded.type;
      }

      await addCommunicationMutation.mutateAsync(data);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(false);
    }
  };

  // Inline add note (no popup)
  const handleInlineNoteSubmit = async () => {
    if (!newNoteContent.trim()) return;
    try {
      setIsAddingNote(true);
      await addCommunicationMutation.mutateAsync({
        content: newNoteContent.trim(),
        metadata: {
          isPinned: newNotePinned,
        },
      });
      setNewNoteContent('');
      setNewNotePinned(false);
    } catch (e) {
      // toast handled in mutation onError
    } finally {
      setIsAddingNote(false);
    }
  };

  const getIcon = () => {
    switch (tab) {
      case 'emails':
        return <Mail className="h-4 w-4" />;
      case 'phone':
        return <Phone className="h-4 w-4" />;
      case 'notes':
        return <FileText className="h-4 w-4" />;
      case 'documents':
        return <FileIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (tab) {
      case 'emails':
        return 'Email';
      case 'phone':
        return 'Phone Call';
      case 'notes':
        return 'Note';
      case 'documents':
        return 'Document';
      default:
        return '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    if (tab === 'documents') {
      setSelectedFile(files[0]);
    } else if (tab === 'emails') {
      setEmailAttachments(Array.from(files));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add UI */}
      {tab !== 'notes' ? (
        <div className="flex justify-end">
          <Button 
            onClick={() => tab === 'emails' ? setShowEmailDialog(true) : setShowAddDialog(true)} 
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add {getTitle()}
          </Button>
        </div>
      ) : (
        <Card className="p-3">
          <div className="space-y-2">
            <Textarea
              ref={noteInputRef}
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Add an internal note..."
              className="min-h-[80px] resize-y"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleInlineNoteSubmit();
                }
              }}
            />
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('p-2', newNotePinned && 'text-yellow-600')}
                onClick={() => setNewNotePinned((v) => !v)}
              >
                <Pin className={cn('h-4 w-4', newNotePinned && 'fill-current')} />
                <span className="ml-2 text-xs">{newNotePinned ? 'Pinned' : 'Pin note'}</span>
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Cmd/Ctrl+Enter to add</span>
                <Button size="sm" onClick={handleInlineNoteSubmit} disabled={isAddingNote || !newNoteContent.trim()}>
                  {isAddingNote ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Add Note
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Communications List */}
      {communications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {tab} found. Add your first {getTitle().toLowerCase()}.
        </div>
      ) : (
        <div className="space-y-3">
          {communications.map((comm) => (
            <Card 
              key={comm.id} 
              className={cn(
                "p-4 transition-colors relative group",
                comm.metadata?.isPinned && tab === 'notes' && "bg-yellow-50 border-yellow-200"
              )}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {tab === 'emails' && (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{comm.subject || 'No subject'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          comm.direction === 'inbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {comm.direction === 'inbound' ? 'Received' : 'Sent'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        {comm.direction === 'inbound' ? 'From:' : 'To:'} {comm.direction === 'inbound' ? comm.from : comm.to}
                      </div>
                    </>
                  )}
                  
                  {tab === 'phone' && (
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{comm.phoneNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        comm.direction === 'inbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {comm.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                      </span>
                      {comm.duration && (
                        <span className="text-sm text-gray-500">• {comm.duration}</span>
                      )}
                    </div>
                  )}

                  {tab === 'notes' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{comm.metadata?.agentName || 'Unknown Agent'}</p>
                          {comm.metadata?.isPinned && (
                            <Badge variant="secondary" className="text-yellow-600">
                              Pinned
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        {format(comm.timestamp.toDate(), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  )}

                  {tab === 'documents' && (
                    <div className="flex items-center gap-2 mb-2">
                      <FileIcon className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{comm.documentName || 'Document'}</span>
                      {comm.documentSize && (
                        <span className="text-sm text-gray-500">({formatFileSize(comm.documentSize)})</span>
                      )}
                      {comm.documentUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(comm.documentUrl, '_blank')}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      )}
                    </div>
                  )}

                  {tab === 'notes' ? (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700">{comm.metadata?.content || comm.content}</p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{comm.content || comm.description}</div>
                  )}
                  
                  {/* Email Attachments */}
                  {tab === 'emails' && comm.attachments && comm.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {comm.attachments.map((attachment, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(attachment.url, '_blank')}
                          className="text-xs"
                        >
                          <Paperclip className="h-3 w-3 mr-1" />
                          {attachment.name}
                          {attachment.size && (
                            <span className="ml-1 text-gray-500">({formatFileSize(attachment.size)})</span>
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {tab !== 'notes' && (
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {comm.metadata?.agentName || comm.createdByEmail || 'Unknown'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(comm.timestamp.toDate(), 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {tab === 'notes' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-8 w-8",
                        comm.metadata?.isPinned 
                          ? "text-yellow-600" 
                          : "opacity-70 hover:opacity-100"
                      )}
                      onClick={() => togglePinMutation.mutate({ id: comm.id, isPinned: comm.metadata?.isPinned || false })}
                    >
                      <Pin className={cn(
                        "h-4 w-4",
                        comm.metadata?.isPinned && "fill-current"
                      )} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteCommunicationMutation.mutate(comm.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Dialog (for non-notes tabs) */}
      {tab !== 'notes' && (
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add {getTitle()}</DialogTitle>
              <DialogDescription>
                Add a new {getTitle().toLowerCase()} to this application.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {tab === 'emails' && (
              <>
                <div>
                  <Label>Direction</Label>
                  <Select
                    value={formData.direction}
                    onValueChange={(value: 'inbound' | 'outbound') => 
                      setFormData({ ...formData, direction: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outbound">Sent</SelectItem>
                      <SelectItem value="inbound">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <Label>{formData.direction === 'inbound' ? 'From' : 'To'}</Label>
                  <Input
                    value={formData.to}
                    onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                    placeholder="Email address"
                    type="email"
                  />
                </div>
                <div>
                  <Label>Attachments</Label>
                  <div className="space-y-2">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="cursor-pointer"
                    />
                    {emailAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {emailAttachments.map((file, idx) => (
                          <Badge key={idx} variant="secondary">
                            <Paperclip className="h-3 w-3 mr-1" />
                            {file.name}
                            <button
                              type="button"
                              onClick={() => setEmailAttachments(emailAttachments.filter((_, i) => i !== idx))}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
              )}
              
              {tab === 'phone' && (
              <>
                <div>
                  <Label>Direction</Label>
                  <Select
                    value={formData.direction}
                    onValueChange={(value: 'inbound' | 'outbound') => 
                      setFormData({ ...formData, direction: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outbound">Outbound</SelectItem>
                      <SelectItem value="inbound">Inbound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="(555) 123-4567"
                    type="tel"
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g., 5 minutes"
                  />
                </div>
              </>
              )}
              
              {tab === 'documents' && (
              <div>
                <Label>Upload Document</Label>
                <div className="space-y-2">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                      id="document-upload"
                    />
                    <label
                      htmlFor="document-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="h-10 w-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX, XLS, XLSX, PNG, JPG up to 10MB
                      </span>
                    </label>
                  </div>
                  {selectedFile && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{selectedFile.name}</span>
                        <span className="text-xs text-gray-500">
                          ({formatFileSize(selectedFile.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              )}
              
              <div>
                <Label>{tab === 'documents' ? 'Description' : 'Content'}</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={
                    tab === 'documents' 
                      ? 'Add a description for this document...' 
                      : `Enter ${getTitle().toLowerCase()} content...`
                  }
                  rows={tab === 'documents' ? 3 : 6}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={
                  uploadingFile ||
                  (tab === 'documents' && !selectedFile) ||
                  (tab !== 'documents' && !formData.content)
                }
              >
                {uploadingFile ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Add {getTitle()}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Email Dialog */}
      {tab === 'emails' && (
        <EmailDialog
          open={showEmailDialog}
          onOpenChange={setShowEmailDialog}
          onSend={handleEmailSend}
          recipientEmail={merchantEmail}
          merchantName={merchantName}
          applicationId={applicationId}
        />
      )}
    </div>
  );
}